import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Helper function to ensure trial record exists for a user
  const ensureTrialRecord = async (userId: string, email: string) => {
    try {
      // Check if subscriber record already exists
      const { data: existingSubscriber } = await supabase
        .from('subscribers')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      if (!existingSubscriber) {
        // Create trial record for new user (7-day free trial without credit card)
        const trialEndsAt = new Date();
        trialEndsAt.setDate(trialEndsAt.getDate() + 7);
        
        const { error: insertError } = await supabase
          .from('subscribers')
          .upsert({
            user_id: userId,
            email: email,
            subscribed: false,
            trial_ends_at: trialEndsAt.toISOString(),
            plan: null,
          }, { onConflict: 'email' });
        
        if (insertError) {
          console.error('Error creating trial record:', insertError);
        } else {
          console.log('Trial record created for new user, expires:', trialEndsAt.toISOString());
        }
      }
    } catch (error) {
      console.error('Error in ensureTrialRecord:', error);
    }
  };

  useEffect(() => {
    let mounted = true;
    
    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) {
        setUser(session?.user ?? null);
        setLoading(false);
        console.log('Auth state changed:', _event, session?.user?.id);
        
        // For new signups (including OAuth), ensure trial record exists
        if (session?.user && (_event === 'SIGNED_IN' || _event === 'USER_UPDATED')) {
          // Use setTimeout to avoid Supabase auth deadlock
          setTimeout(() => {
            ensureTrialRecord(session.user.id, session.user.email || '');
          }, 0);
        }
      }
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (mounted) {
        if (error) {
          console.error('Error getting session:', error);
        }
        setUser(session?.user ?? null);
        setLoading(false);
        console.log('Initial session check:', session?.user?.id);
        
        // Also ensure trial record for existing sessions (covers OAuth users who just signed up)
        if (session?.user) {
          setTimeout(() => {
            ensureTrialRecord(session.user.id, session.user.email || '');
          }, 0);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    console.log('AuthProvider: Tentando fazer login...');
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      console.error('AuthProvider: Erro no signIn:', error);
      throw error;
    }
    
    console.log('AuthProvider: Login realizado com sucesso:', data.user?.id);
  };

  const signUp = async (email: string, password: string, name?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: name,
        },
      },
    });
    if (error) throw error;

    // Create trial record for new user (7-day free trial without credit card)
    if (data.user) {
      const trialEndsAt = new Date();
      trialEndsAt.setDate(trialEndsAt.getDate() + 7);
      
      const { error: insertError } = await supabase
        .from('subscribers')
        .upsert({
          user_id: data.user.id,
          email: email,
          subscribed: false,
          trial_ends_at: trialEndsAt.toISOString(),
          plan: null,
        }, { onConflict: 'email' });
      
      if (insertError) {
        console.error('Error creating trial record:', insertError);
      } else {
        console.log('Trial record created, expires:', trialEndsAt.toISOString());
      }
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};